import React, {FC, memo, ReactNode, useCallback, useState} from 'react';
import {animated, useTransition} from 'react-spring';
import styled, {css} from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {EventHandler, RendererOf} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  CommentAnnotationModel,
  MessageAnnotationModel,
  RMessageAnnotation,
} from '../../../../../../libs/shared/core/src/models/api/annotationModel';
import {AttachmentModel} from '../../../../../../libs/shared/core/src/models/api/attachmentModel';
import {TeammateModel} from '../../../../../../libs/shared/core/src/models/api/teammateModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TeammateAvatar} from '../../../../../../libs/shared/ui/src/components/avatar/teammateAvatar';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {IconButton} from '../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {RelativeDateTimeFormatsEnum} from '../../../../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {TooltipCoordinator} from '../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useMeasureElement} from '../../../../../../libs/shared/ui/src/hooks/domHookHelpers';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {CommentReactionVisualProps} from '../../../../../../srcCommon/inboxes/viewer/activity/comment/commentViewerSelectors';
import {PermissionCheck} from '../../../../../../srcCommon/permissions/permissionCheck';
import {PermissionDenied} from '../../../../../../srcCommon/permissions/permissionDenied';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {renderCommentTitle} from '../../../../../helpers/commentViewerHelpers';
import {PermissionResult} from '../../../../../helpers/permissionHelpers';
import {CommentActivityModel, isActivityPinned} from '../../../../../models/api/activityModel';
import {CommentModel} from '../../../../../models/api/commentModel';
import {VisualRecipientModel} from '../../../../../models/api/visualRecipientModel';
import {makeGetAnnotation} from '../../../../../store/data/annotations/annotationsSelectors';
import {InteractiveDateTimeFormatter} from '../../../../common/formatters/dateTime/interactiveDateTimeFormatter';
import {StyledAttachmentDiv, StyledImagePreviewWrapper} from '../../activity/attachments/attachmentBase';
import {StyledAttachmentBase} from '../../activity/attachments/commentAttachment';
import {
  CommentReactionRendererProps,
  CommentReactions,
  StyledReactionButton,
} from '../../activity/comment/commentBaseReactions';
import {
  CommentMarkdownRendererProps,
  StyledAnnotationSummaryButton,
  StyledAttachmentMarginTopDiv,
  StyledCommentDiv,
  StyledEmojiDiv,
  StyledReferencedCommentSummaryButton,
  StyledSingleImageDiv,
} from '../../activity/comment/commentBody';
import {CommentBodyWithUnfurls} from '../../activity/comment/commentBodyWithUnfurls';
import {maybeRenderAnnotationsHighlightViewerWrapper} from '../../activity/comment/commentViewer';
import {StyledUnfurlDiv} from '../../activity/comment/commentViewerBaseUnfurl';
import {StyledCommentViewerPreviewImg} from '../../activity/comment/commentViewerSingleImageAttachmentPreview';
import {StyledCommentViewerUnfurlsContainer} from '../../activity/comment/commentViewerUnfurls';

/*
 * Props.
 */

export type ConversationSummaryCommentClickHandler = (activityId: number, conversationId: number) => void;

interface ConversationSummaryCommentViewerProps {
  panelId: ViewerPanelId;
  activity: CommentActivityModel;
  comment: CommentModel;
  author: TeammateModel;
  referencedComment: CommentModel | undefined;
  referencedAuthor: TeammateModel | undefined;
  referencedRecipient: VisualRecipientModel | undefined;
  attachments: Array<AttachmentModel>;
  annotations: ReadonlyArray<CommentAnnotationModel>;
  reactions?: Array<CommentReactionVisualProps>;
  userId: number;
  userAlias: string;
  mentionableAliases: ReadonlySet<string>;
  onJumpClick: ConversationSummaryCommentClickHandler;
  onPinClick: ConversationSummaryCommentClickHandler;
  onUnpinClick: ConversationSummaryCommentClickHandler;
  namespace: string | undefined;
  onQuoteInCommentClick?: (model: MessageAnnotationModel | CommentAnnotationModel) => void;
}

/*
 * Style.
 */

const defaultUnpinAnimationDuration = 0.4; // Seconds

const StyledCommentViewerAttachmentsContainer = styled.div``;

const StyledUnpinIconWrapperDiv = styled.div`
  display: none;
  position: relative;
  justify-self: end;
`;

const StyledUnpinIconDiv = styled.div`
  position: absolute;
  top: -14px;
  left: -22px;
`;

const StyledCommentHeaderContainerDiv = styled.div`
  display: grid;
  grid-template-columns: 20px auto minmax(22px, auto);
  align-items: center;
  font-size: ${fontSizes.verySmall};
  padding-top: 1px;
  margin-bottom: 8px;
`;

const StyledCommentTimeSpan = styled.span`
  display: block;
  text-transform: uppercase;
  justify-self: end;
  color: ${(p) => p.theme.greys.shade60};
`;

const commentHorizontalPadding = 12;

interface CommentContainerButtonProps {
  $isReply: boolean;
}

const StyledCommentContainerButton = styled(ChromelessButton)<CommentContainerButtonProps>`
  display: flex;
  flex-direction: column;
  background-color: ${(p) => p.theme.greys.shade20};
  border-radius: 15px;
  padding: 12px ${commentHorizontalPadding}px 12px;

  &:hover {
    background-color: ${(p) => p.theme.greys.shade30};

    ${StyledUnpinIconWrapperDiv} {
      display: block;
    }

    ${StyledCommentTimeSpan} {
      display: none;
    }
  }

  ${StyledAttachmentMarginTopDiv} {
    margin: 0px;
  }

  ${StyledSingleImageDiv} {
    padding: ${(p) => (p.$isReply ? '8px 0px 4px' : '4px 0px')};
  }

  ${StyledCommentViewerPreviewImg} {
    max-height: 60px;
    min-width: 50px;
    border-radius: 8px;
  }

  ${StyledImagePreviewWrapper} {
    margin: 0px;
    padding: 4px;
  }

  ${StyledUnfurlDiv} {
    margin: 10px 0px 0px;
    width: 100%;
    border: 1px solid ${(p) => p.theme.greys.shade50};
  }

  ${StyledAttachmentDiv} {
    margin: 4px 0px 5px 0px;
  }

  ${StyledAttachmentBase} {
    padding: 0 3px;
  }

  ${StyledReferencedCommentSummaryButton} {
    padding: 4px 6px 5px;
    border-bottom: 1px solid ${(p) => p.theme.greys.shade40};
    margin: -5px -${commentHorizontalPadding}px 2px;
  }

  ${StyledCommentViewerAttachmentsContainer} {
    margin-bottom: -1px;
  }

  ${StyledCommentViewerUnfurlsContainer} {
    padding-bottom: 5px;
  }
`;

const StyledTitleSpan = styled.span`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.semibold};
  padding-left: 8px;
  ${ellipsis()};
`;

const StyledAvatarDiv = styled.div`
  margin-top: -1px;
`;

const StyledReactionsDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;

  padding-top: 3px;
  margin-bottom: -5px;
`;

const StyledCommentMarkdownDiv = styled(StyledCommentDiv)`
  line-height: 20px;
  padding: 0px;

  ${(p) => p.$isReply && getReplyToCommentStyles()}
`;

function getReplyToCommentStyles() {
  return css`
    padding: 4px 0px 0px;
    margin-left: -2px;
  `;
}

const StyledUnpinnedTombstoneDiv = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${(p) => p.theme.foreground};
  border: 1px solid ${(p) => p.theme.greys.shade20};
  border-radius: 15px;
  padding: 12px 15px;
`;

const StyledTombstoneMessageSpan = styled.span`
  max-width: 225px;
  line-height: 20px;
`;

const StyledRepinButton = styled(ChromelessButton)`
  font-weight: ${fontWeights.bold};
  font-size: 11px;
  border-radius: 13px;
  line-height: 26px;
  padding: 0 8px;
  text-transform: uppercase;

  &:hover {
    background-color: ${(p) => p.theme.greys.shade20};
  }

  ${(p) =>
    p.isDisabled &&
    css`
      color: ${p.theme.greys.shade40};

      &:hover {
        background-color: ${p.theme.alphas.transparent};
      }
    `};
`;

const StyledConversationSummaryEmojiDiv = styled(StyledEmojiDiv)`
  margin-top: 4px;
`;

const StyledImageAttachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-left: -4px;
  margin-bottom: 1px;
`;

const StyledConversationSummaryReactionButton = styled(StyledReactionButton)`
  background-color: ${(p) => p.theme.alphas.transparent};
  padding: 0px 4px 0px 0px;
  margin-top: -4px;
`;

interface HeightAnimationContainerDivProps {
  $height: number;
  $transitionDuration: number;
}
const StyledHeightAnimationContainerDiv = styled.div<HeightAnimationContainerDivProps>`
  position: relative;
  height: ${(p) => p.$height}px;
  transition: height ${(p) => p.$transitionDuration}s;

  ${StyledAnnotationSummaryButton} {
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 0;
    margin-top: 8px;
  }

  ${StyledAnnotationSummaryButton} + div:not(:empty) {
    margin-top: 11px;
  }
`;

const StyledOpacityTransitionDiv = styled(animated.div)`
  position: absolute;
  width: 100%;
`;

/*
 * Component.
 */

export const ConversationSummaryCommentViewer: FC<ConversationSummaryCommentViewerProps> = memo((props) => {
  const {activity, attachments, onJumpClick, onPinClick, onUnpinClick, referencedAuthor, comment} = props;
  const shouldShowTombstone = !isActivityPinned(props.activity);

  /*
   * Hooks.
   */

  // The animation from Comment -> Tombstone has a smooth height transition
  // which requires knowing the 2 elements heights, which we grab here.
  const [tombstoneRef, {height: tombstoneHeight}] = useMeasureElement();
  const [commentRef, {height: commentHeight}] = useMeasureElement();

  // The Comment -> Tombstone animation does an opactity cross-fade between the two elements using this hook.
  // useTransition is nice enough to complete the animation and then remove the now invisible component from the tree.
  const opacityTransitions = useTransition(shouldShowTombstone, {
    initial: {opacity: 1},
    from: {opacity: 0},
    enter: {opacity: 1},
    leave: {opacity: 0},
    config: {duration: defaultUnpinAnimationDuration * 1000},
  });

  // The comment's 'height' property is transitioned to provide a smooth animation between unpinning and repinning.
  // Unfortunately, the 'height' is 0 on the first render and occasionally bumped as the layout adjusts.
  // We don't want to animate those initial changes of layout, so we disable the transition until the user clicks "unpin",
  // which is the first time we actually would need the transition animation.
  const [unpinAnimationDuration, setUnpinAnimationDuration] = useState(0);
  const enableAnimations = useCallback(() => {
    setUnpinAnimationDuration(defaultUnpinAnimationDuration);
  }, [setUnpinAnimationDuration]);

  const onUnpinButtonClick: EventHandler = useCallback(
    (event) => {
      trackInterfaceEvent(InterfaceEventsEnum.CONVERSATION_SUMMARY_COMMENT_PIN_ICON_CLICK, {
        conversation_id: activity.conversationId,
        activity_id: activity.id,
        has_attachment: attachments.length > 0,
        action: 'unpin',
      });

      enableAnimations();
      onUnpinClick(activity.id, activity.conversationId);
      event.preventDefault();
    },
    [activity, attachments, onUnpinClick, enableAnimations],
  );

  const onRepinButtonClick = useCallback(() => {
    trackInterfaceEvent(InterfaceEventsEnum.CONVERSATION_SUMMARY_COMMENT_PIN_ICON_CLICK, {
      conversation_id: activity.conversationId,
      activity_id: activity.id,
      has_attachment: attachments.length > 0,
      action: 'pin',
    });

    onPinClick(activity.id, activity.conversationId);
  }, [activity, attachments, onPinClick]);

  const annotation = useAppSelector(makeGetAnnotation<void>(() => comment.annotationUid));

  const onCommentContainerClick: EventHandler = useCallback(
    (event) => {
      // Don't do anything if the unpin button was clicked.
      if (event.defaultPrevented) {
        return;
      }

      trackInterfaceEvent(InterfaceEventsEnum.CONVERSATION_SUMMARY_PINNED_COMMENT_CLICK, {
        conversation_id: activity.conversationId,
        activity_id: activity.id,
        has_attachment: attachments.length > 0,
        is_message_comment: annotation && RMessageAnnotation.is(annotation),
      });

      // Jump to the comment in the P3 timeline.
      onJumpClick(activity.id, activity.conversationId);
    },
    [activity.conversationId, activity.id, annotation, attachments.length, onJumpClick],
  );

  /*
   * Render functions.
   */

  const renderTitle = () => (
    <StyledTitleSpan>
      {renderCommentTitle(props.author, referencedAuthor, props.panelId, props.userId)}
    </StyledTitleSpan>
  );

  const renderCommentDate = () => (
    <StyledCommentTimeSpan>
      <InteractiveDateTimeFormatter
        relativeFormat={RelativeDateTimeFormatsEnum.SHORT}
        value={props.comment.sentAt}
      />
    </StyledCommentTimeSpan>
  );

  const renderAuthorAvatar = () => (
    <StyledAvatarDiv>
      <TeammateAvatar teammate={props.author} size={VisualSizesEnum.MEDIUM} />
    </StyledAvatarDiv>
  );

  const renderCommentHeader = () => (
    <StyledCommentHeaderContainerDiv>
      {renderAuthorAvatar()}
      {renderTitle()}
      {renderTopRightCorner()}
    </StyledCommentHeaderContainerDiv>
  );

  // These swap on hover
  const renderTopRightCorner = () => (
    <>
      {renderCommentDate()}
      {renderUnpinButton()}
    </>
  );

  const renderUnpinButton = () => (
    <UserThemeConsumer>
      {(theme) => (
        <StyledUnpinIconWrapperDiv>
          <PermissionCheck>
            {(permissionResult) => (
              /* This div is necessary for absolute positioning + tooltip positioning */
              <StyledUnpinIconDiv>
                <IconButton
                  isDisabled={!permissionResult.isAllowed}
                  renderTooltip={makeRenderPermissionDeniedTooltip(permissionResult)}
                  iconName="unpinOutlineSmall"
                  onClick={onUnpinButtonClick}
                  label={<Trans id="conversation-summary-unpin-activity">Unpin</Trans>}
                  hoverColor={theme.greys.shade80}
                  backgroundHoverColor={theme.alphas.gray10}
                />
              </StyledUnpinIconDiv>
            )}
          </PermissionCheck>
        </StyledUnpinIconWrapperDiv>
      )}
    </UserThemeConsumer>
  );

  const makeRenderPermissionDeniedTooltip = (permissionResult: PermissionResult) => {
    // Use the IconButton's default tooltip.
    if (permissionResult.isAllowed) {
      return undefined;
    }

    // Say why the button is disabled.
    const renderTooltip = () => <PermissionDenied reason={permissionResult.reason} />;
    return renderTooltip;
  };

  const renderCommentBody = () => {
    const textWrapper = (child: ReactNode) =>
      maybeRenderAnnotationsHighlightViewerWrapper(child, props.panelId, props.comment, props.annotations);

    return (
      <CommentBodyWithUnfurls
        comment={props.comment}
        referencedAuthor={referencedAuthor}
        referencedComment={props.referencedComment}
        referencedRecipient={props.referencedRecipient}
        mentionableAliases={props.mentionableAliases}
        userAlias={props.userAlias}
        attachments={attachments}
        shouldAlwaysUseMultiAttachmentStyle
        renderCommentMarkdown={renderCommentMarkdown}
        renderOneBigEmoji={renderOneBigEmoji}
        renderImageAttachments={renderImageAttachments}
        panelId={props.panelId}
        isInteractionDisabled
        noMargin
        shouldTruncateAnnotationText
        namespace={props.namespace}
        textWrapper={textWrapper}
        isJumpPointerDisabled
      />
    );
  };

  const maybeRenderCommentReactions = () => {
    if (!props.reactions?.length) {
      return null;
    }

    return (
      <StyledReactionsDiv>
        <CommentReactions
          reactions={props.reactions}
          userId={props.userId}
          renderCommentReaction={renderCommentReaction}
        />
      </StyledReactionsDiv>
    );
  };

  const renderUnpinnedTombstone = () => (
    <StyledUnpinnedTombstoneDiv ref={tombstoneRef}>
      <StyledTombstoneMessageSpan>
        <Trans id="conversation-summary-unpinned-comment-tombstone">
          Comment unpinned but still lives in the conversation
        </Trans>
      </StyledTombstoneMessageSpan>

      {renderTombstoneUnpinnedButton()}
    </StyledUnpinnedTombstoneDiv>
  );

  const renderTombstoneUnpinnedButton = () => (
    <PermissionCheck>
      {(permissionResult) => (
        <TooltipCoordinator render={makeRenderPermissionDeniedTooltip(permissionResult)}>
          <StyledRepinButton isDisabled={!permissionResult.isAllowed} onClick={onRepinButtonClick}>
            <Trans id="conversation-summary-repin-comment">Undo</Trans>
          </StyledRepinButton>
        </TooltipCoordinator>
      )}
    </PermissionCheck>
  );

  const renderCommentContainer = () => (
    <StyledCommentContainerButton
      $isReply={Boolean(referencedAuthor)}
      onClick={onCommentContainerClick}
      ref={commentRef}
    >
      {renderCommentHeader()}
      {renderCommentBody()}
      {maybeRenderCommentReactions()}
    </StyledCommentContainerButton>
  );

  // The Comment -> Tombstone animation has 2 parts:
  //    1. The height transition between the two elements, which is actually just transitioning the absolute height of
  //       the container element for both of them: StyledHeightAnimationContainerDiv.
  //    2. An opacity cross-fade, which is handled by the useTransition hook, where the opacity styles are
  //       injected in the StyledOpacityTransitionDiv's below.
  return (
    <StyledHeightAnimationContainerDiv
      $height={shouldShowTombstone ? tombstoneHeight : commentHeight}
      $transitionDuration={unpinAnimationDuration}
    >
      {opacityTransitions((style, shouldRenderTombstone) =>
        shouldRenderTombstone ? (
          <StyledOpacityTransitionDiv key="tombstone" style={style}>
            {renderUnpinnedTombstone()}
          </StyledOpacityTransitionDiv>
        ) : (
          <StyledOpacityTransitionDiv key="comment" style={style}>
            {renderCommentContainer()}
          </StyledOpacityTransitionDiv>
        ),
      )}
    </StyledHeightAnimationContainerDiv>
  );
});

/*
 * Renderers.
 */

const renderCommentMarkdown: RendererOf<CommentMarkdownRendererProps> = (props) => {
  const {newMultiAttachmentStyle, isReply, markdownNode} = props;
  return (
    <StyledCommentMarkdownDiv $newMultiAttachmentStyle={newMultiAttachmentStyle} $isReply={isReply}>
      {markdownNode}
    </StyledCommentMarkdownDiv>
  );
};

const renderImageAttachments: RendererOf<ReactNode> = (imageAttachmentsNode) => (
  <StyledImageAttachments>{imageAttachmentsNode}</StyledImageAttachments>
);

const renderCommentReaction: RendererOf<CommentReactionRendererProps> = (props) => (
  <StyledConversationSummaryReactionButton
    $isUserSelected={props.isUserSelected}
    $isDisabled={props.isDisabled}
    isDisabled={props.isDisabled}
    onClick={props.onClick}
  >
    {props.renderReactionNode(14)}
  </StyledConversationSummaryReactionButton>
);

const renderOneBigEmoji: RendererOf<ReactNode> = (emojiNode) => (
  <StyledConversationSummaryEmojiDiv>{emojiNode}</StyledConversationSummaryEmojiDiv>
);
